import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import maplibregl, { type Map as MapLibreMap } from "maplibre-gl";

import { ListingCard } from "@/listings/ListingCard.tsx";
import type { Anchor, Listing } from "@housing/shared";

/**
 * Desktop detail view: a real MapLibre popup so the card stays pinned to its dot
 * while you pan, with the React tree portalled into the popup's DOM node. The
 * mobile equivalent is a bottom sheet — see App.
 */
/** Gap between the dot and the card's edge. */
const TIP = 14;
/** Smallest gap left above the card when the window is too short to centre it. */
const TOP_MARGIN = 16;

export function ListingPopup({
  map,
  listing,
  anchor,
  onClose,
}: {
  map: MapLibreMap;
  listing: Listing;
  anchor: Anchor | null;
  onClose: () => void;
}) {
  const container = useMemo(() => document.createElement("div"), []);
  const popupRef = useRef<maplibregl.Popup | null>(null);

  useEffect(() => {
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      // The card sizes itself (~480px); MapLibre's own 240px max would clip it.
      maxWidth: "none",
      offset: TIP,
      // Always above the dot — never MapLibre's automatic choice.
      //
      // Auto re-decides on every map move, and it decides from where the dot is
      // *now*. A dot in the top half of the screen therefore opens its card
      // below itself, and then the ease below slides the dot down past the
      // threshold and the card jumps to the other side of it mid-animation.
      // Nothing about that flip is useful: the ease exists precisely to make
      // room above, so the answer is known before the popup is ever added.
      anchor: "bottom",
      focusAfterOpen: false,
    })
      .setLngLat([listing.lon, listing.lat])
      .setDOMContent(container)
      .addTo(map);
    popupRef.current = popup;
    // No "close" listener on purpose. closeButton/closeOnClick are both off, so
    // the only thing that ever closes this popup is the cleanup below — and
    // wiring close → onClose would make StrictMode's mount/cleanup/mount cycle
    // clear the selection the instant it was made.

    // Nudge the map so the whole card is on screen. The card sits above its dot
    // and stays there, so the dot has to be pushed far enough below centre to
    // clear the card's height — and that height varies with the photo, the
    // travel legs and the window, which is why it is measured here rather than
    // guessed at. Guess low and the card simply runs off the top of the screen.
    //
    // The card is already in `container` by now: the portal below commits
    // before this effect runs, and `addTo` has just put it in the document.
    const viewport = map.getContainer().clientHeight;
    const height = container.offsetHeight;
    const top = Math.max(TOP_MARGIN, (viewport - height - TIP) / 2);
    map.easeTo({
      center: [listing.lon, listing.lat],
      duration: 320,
      offset: [0, top + height + TIP - viewport / 2],
    });

    return () => {
      popup.remove();
      popupRef.current = null;
    };
  }, [map, listing.lon, listing.lat, container]);

  return createPortal(
    <ListingCard listing={listing} anchor={anchor} onClose={onClose} variant="popup" />,
    container,
  );
}
